import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import { chargeOperationsReport } from "../../services/LegalFileService";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import FileUploadBox from "../General/FileUploadBox";
import { useToast } from "../../context/ToastContext";

export default function ReportOperationsFile() {
  const { showToast } = useToast();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [openHelp, setOpenHelp] = useState(false);

  const handleFileSelect = (file) => {
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["xls", "xlsx"].includes(ext)) {
        setErrors({ file: "Solo se permiten archivos .xls o .xlsx" });
        setSelectedFile(null);
        return;
      }
      setErrors({});
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setLoading(true);
    try {
      const response = await chargeOperationsReport(formData);

      if (response.data.status === "success") {
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.file}`;
        link.download = response.data.filename || "reporte_operaciones_cedulas.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Reporte generado correctamente.", "success")
        setSelectedFile(null);
      } else {
        showToast("Error al generar el reporte.", "error")
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          apiErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(apiErrors);
      } else {
        showToast(
          error.response?.data?.message || "Ocurrió un error inesperado.",
        "error")
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Reporte de Operaciones para Cédulas"
            description="Subí un Excel con los códigos de expediente para obtener los números de operación necesarios para generar cédulas masivas."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel con Códigos de Expediente"
                  helperText="O arrastrá el archivo Excel aquí (.xls, .xlsx)"
                />
                {errors.file && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                 
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Reporte"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe tener los códigos de expediente en la primera columna (una por fila). El sistema devolverá un Excel con 5 columnas: código del expediente, N° de operación 1 (Admisión de la demanda fiscal), N° de operación 2 (Demanda ejecutivo fiscal), nombre del tipo operación 1 y nombre del tipo operación 2."
        columns={["Código de Expediente"]}
        example={["12345678"]}
      />
    </Grid>
  );
}
